import { Button, Callout, Link } from '@/components/Elements';
import { CheckBox, InputField, PasswordField } from '@/components/Form';
import { usePostUserProfile } from '@/features/setup/api/postUserProfile';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ProfileFormProps } from '@/features/setup/types';
import { getCurrentLanguage } from '@/lib/i18n/getCurrentLanguage';

export const AdminInitiatedProfileForm = ({
  redirectUrlOnSuccess,
}: ProfileFormProps) => {
  const { t } = useTranslation();
  const { mutate, isPending, error } = usePostUserProfile();
  const errorMessages = error?.response?.data?.errors;

  const [firstname, setFirstname] = useState<string>('');
  const [lastname, setLastname] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [passwordConfirmation, setPasswordConfirmation] = useState<string>('');
  // 入力していて、かつパスワードとパスワード確認が一致しているか
  const isValidPassword =
    password && passwordConfirmation && password === passwordConfirmation;

  const [confirmTermsOfService, setConfirmTermsOfService] = useState(false);
  const [confirmPrivacyPolicy, setConfirmPrivacyPolicy] = useState(false);

  const isTermsAgreed = confirmTermsOfService && confirmPrivacyPolicy;

  const canSubmit =
    !!isValidPassword && !!firstname && !!lastname && isTermsAgreed;

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // 重複送信防止: 条件チェックと送信中状態の確認
    if (!canSubmit || isPending) return;

    mutate(
      {
        firstname,
        lastname,
        password,
        passwordConfirmation,
        isTermsAgreed,
      },
      {
        onSuccess: () => {
          window.location.href = redirectUrlOnSuccess;
        },
      }
    );
  };

  const renderNameFields = () => {
    // 言語に応じたフィールドの順序を定義
    const isWesternStyle = !['ja', 'ko', 'zh'].includes(getCurrentLanguage());
    const westernOrderedFields = [
      <InputField
        key="firstname"
        label={t('名') + ' *'}
        autoComplete="given-name"
        placeholder={t('氏名（名）を入力...')}
        value={firstname}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setFirstname(e.currentTarget.value)
        }
        error={!!errorMessages?.firstname}
        errors={errorMessages?.firstname}
        required
      />,
      <InputField
        key="lastname"
        label={t('姓') + ' *'}
        autoComplete="family-name"
        placeholder={t('氏名（姓）を入力...')}
        value={lastname}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setLastname(e.currentTarget.value)
        }
        error={!!errorMessages?.lastname}
        errors={errorMessages?.lastname}
        required
      />,
    ];

    return isWesternStyle
      ? westernOrderedFields
      : westernOrderedFields.reverse();
  };

  return (
    <form className="w-full space-y-6" onSubmit={onSubmit}>
      <div className="space-y-2">
        <h1 className="text-xl font-bold text-gray-900">{t('本登録ページ')}</h1>
        <Callout variant="warning" className="px-3 py-2">
          {t('まだ本登録は完了していません')}
        </Callout>
      </div>
      {renderNameFields()}

      <h2>{t('パスワード')}</h2>
      <div className="text-sm text-gray-600 md:text-sm">
        {t(
          '8文字以上128文字以下で、大文字（A-Z）、小文字（a-z）、数字（0-9）、特殊文字（#?!@$%^&*-）をそれぞれ1文字以上含むよう設定してください。'
        )}
      </div>

      <PasswordField
        label={t('パスワード') + ' *'}
        autoComplete="new-password"
        placeholder={t('新しいパスワードを入力...')}
        value={password}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setPassword(e.currentTarget.value)
        }
        error={!!errorMessages?.password}
        errors={errorMessages?.password}
        required
      />

      <PasswordField
        label={t('パスワード（確認用）') + ' *'}
        autoComplete="new-password"
        placeholder={t('新しいパスワードを再入力...')}
        value={passwordConfirmation}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setPasswordConfirmation(e.currentTarget.value)
        }
        error={!!password && !isValidPassword}
        errors={
          !!password && !isValidPassword
            ? [t('パスワードが一致しません。')]
            : []
        }
        required
      />

      <hr />

      <div>
        <CheckBox
          label={
            <Trans
              i18nKey="agree-terms-and-conditions"
              components={{
                l: (
                  <Link
                    to={t('terms-and-conditions-url')}
                    target="_blank"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {t('利用規約')}
                  </Link>
                ),
              }}
            />
          }
          enabled={confirmTermsOfService}
          setEnabled={() => setConfirmTermsOfService(!confirmTermsOfService)}
        />
        <CheckBox
          label={
            <Trans
              i18nKey="agree-privacy-policy"
              components={{
                l: (
                  <Link
                    to={t('privacy-policy-url')}
                    target="_blank"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {t('プライバシーポリシー')}
                  </Link>
                ),
              }}
            />
          }
          enabled={confirmPrivacyPolicy}
          setEnabled={() => setConfirmPrivacyPolicy(!confirmPrivacyPolicy)}
        />
      </div>

      <Button
        type="submit"
        size="sm"
        className="w-full"
        disabled={!canSubmit || isPending}
        isLoading={isPending}
      >
        {t('送信')}
      </Button>
    </form>
  );
};
